import { useQuery } from '@tanstack/react-query';
import { useRevisionHolds } from '@/features/booking-quarantine';
import { useApi } from '@/shared/api';
import { canAccess, ROLE_GROUPS, shellCapabilities, useAuth } from '@/shared/auth';
import type { NavCountKey } from './adminNavigation';

/** Eine Zahl, oder `error`, wenn sie nicht geladen werden konnte – das darf nie wie „0“ aussehen. */
export type NavCountValue = number | 'error';
export type NavCounts = Partial<Record<NavCountKey, NavCountValue>>;

const COUNT_POLL_MS = 60_000;

function countOf<T>(
  query: { data?: T; isError: boolean; fetchStatus: string },
  pick: (data: T) => number,
): NavCountValue | undefined {
  if (query.data !== undefined) return pick(query.data);
  return query.isError || query.fetchStatus === 'paused' ? 'error' : undefined;
}

/**
 * Zähler neben Navigationseinträgen, aus Abfragen, die es schon gibt: nicht beantwortete
 * Unterhaltungen (dieselbe Abfrage wie früher die Glocke, alle 30 s) und Kanalbuchungen in Quarantäne (jede
 * Minute und beim Zurückkehren ins Fenster). Wartende Revisionen klären sich selbst und zählen
 * nicht mit. Wer die Fläche nicht sehen darf, fragt nicht an.
 */
export function useNavCounts(): NavCounts {
  const api = useApi();
  const { user } = useAuth();
  const staff = canAccess(user, ROLE_GROUPS.staff);
  const inboxEnabled = staff && shellCapabilities(user).inbox;
  const unread = useQuery({
    queryKey: ['inbox', 'unread'],
    queryFn: () => api.messaging.unread(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled: inboxEnabled,
  });
  const holds = useRevisionHolds(staff, COUNT_POLL_MS);
  return {
    inboxAwaitingReply: inboxEnabled ? countOf(unread, (data) => data.awaitingReply) : undefined,
    revisionHolds: staff ? countOf(holds, (data) => data.quarantinedTotal) : undefined,
  };
}

/** Text für Vorlese-Namen und Tooltips, wo der Zähler nicht selbst im Text steht. */
export function countLabel(label: string, value: NavCountValue | undefined): string {
  if (value === undefined || value === 0) return label;
  return value === 'error' ? `${label}, Anzahl nicht geladen` : `${label}, ${value} offen`;
}
